import React, { useContext, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import {
  useCheckEntityDuplicate,
  useCreateEntity,
} from '../../../api/bitedb/entities/entities.hooks';
import { DuplicateEntity } from 'api/bitedb/entities/entities';

interface ISingleEntityFormProps {
  database: any;
  handleClose: () => void;
  handleRefresh?: any;
  onEntityAdded?: () => void;
  onDuplicatesDetected?: (payload: {
    duplicates: DuplicateEntity[];
    fallbackEntities: any[];
  }) => void;
}

/**
 * The detailed, single-entity form. Extracted from AddSingleSupplierModal so it
 * can be reused both as a standalone dialog and inside the tabbed Add Entity
 * modal (alongside the quick-add-multiple grid).
 */
export const SingleEntityForm: React.FC<ISingleEntityFormProps> = ({
  database,
  handleClose,
  handleRefresh,
  onEntityAdded,
  onDuplicatesDetected,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const createEntityMutation = useCreateEntity();
  const checkDuplicateMutation = useCheckEntityDuplicate();
  const [ supplierName, setSupplierName ] = useState<string>('');
  const [ alias, setAlias ] = useState<string>('');
  const [ supplierDescription, setSupplierDescription ] = useState<string>('');
  const [ contactEmail, setContactEmail ] = useState<string>('');
  const [ contactPhone, setContactPhone ] = useState<string>('');
  const [ address, setAddress ] = useState<string>('');
  const [ address1, setAddress1 ] = useState<string>('');
  const [ address2, setAddress2 ] = useState<string>('');
  const [ country, setCountry ] = useState<string>('');
  const [ riskLevel, setRiskLevel ] = useState<string>('Low');
  const [ category ] = useState<string>('Supplier');
  const [ subCategory ] = useState<string>('Supplier');
  const [ entityType, setEntityType ] = useState<string>('company');
  const [ businessUnit, setBusinessUnit ] = useState<string>('');
  const [ parentBusiness, setParentBusiness ] = useState<string>('');
  const [ skuProductId, setSkuProductId ] = useState<string>('');
  const [ supplierVendorId, setSupplierVendorId ] = useState<string>('');
  const [ businessRegistrationNumber, setBusinessRegistrationNumber ] = useState<string>('');
  const [ personId, setPersonId ] = useState<string>('');
  const [ manufacturerIdentificationNumber, setManufacturerIdentificationNumber ] = useState<string>('');
  const [ domain, setDomain ] = useState<string>('');

  const resetFields = () => {
    setSupplierName('');
    setAlias('');
    setSupplierDescription('');
    setContactEmail('');
    setContactPhone('');
    setAddress('');
    setAddress1('');
    setAddress2('');
    setCountry('');
    setRiskLevel('Low');
    setEntityType('company');
    setBusinessUnit('');
    setParentBusiness('');
    setSkuProductId('');
    setSupplierVendorId('');
    setBusinessRegistrationNumber('');
    setPersonId('');
    setManufacturerIdentificationNumber('');
    setDomain('');
  };

  const handleCancel = () => {
    resetFields();
    handleClose();
  };

  const handleSupplierAddition = async() => {
    if (createEntityMutation.isPending) {
      return;
    }

    const trimmedName = supplierName.trim();

    const supplier = {
      name: trimmedName,
      alias: alias || undefined,
      description: supplierDescription,
      email: contactEmail,
      category: category,
      entity_type: entityType,
      phone: contactPhone,
      address: address,
      address1: address1 || undefined,
      address2: address2 || undefined,
      country_of_origin: country,
      country_of_origin_confidence: 'High',
      risk_level: riskLevel,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      subCategory: subCategory,
      business_unit: businessUnit,
      parent_business: parentBusiness,
      sku_product_id: skuProductId,
      supplier_vendor_id: supplierVendorId,
      business_registration_number: businessRegistrationNumber,
      person_id: personId,
      manufacturer_identification_number: manufacturerIdentificationNumber,
      domain: domain,
    };
    if (!trimmedName) {
      enqueueSnackbar('Please provide a name for the entity', {
        variant: 'error',
      });
      return;
    }
    if (!database?.id) {
      enqueueSnackbar('Database information missing', { variant: 'error' });
      return;
    }

    const reqBody = {
      ...supplier,
      database_id: database.id,
      created_by: cur_user.basics?.id,
    };

    const duplicatePayload = {
      name: trimmedName,
      country_of_origin: country?.trim() || undefined,
      database_id: database.id,
    };

    try {
      const duplicateResult = await checkDuplicateMutation.mutateAsync({
        payload: duplicatePayload,
        teamId: cur_user.selected_team?.id,
      });
      const duplicateList = Array.isArray(duplicateResult?.duplicates) ? duplicateResult.duplicates : [];
      if (duplicateList.length > 0) {
        enqueueSnackbar(
          `${duplicateList.length} potential duplicate${duplicateList.length > 1 ? 's' : ''} detected. Review before adding.`,
          { variant: 'warning' },
        );
        onDuplicatesDetected?.({
          duplicates: duplicateList,
          fallbackEntities: [ reqBody ],
        });
        resetFields();
        handleClose();
        return;
      }
    } catch (error) {
      console.error('Error checking duplicates before add:', error);
    }

    try {
      const response = await createEntityMutation.mutateAsync({
        entityData: reqBody,
        teamId: cur_user.selected_team?.id,
      });
      const duplicates = Array.isArray(response?.duplicates) ? response.duplicates : [];
      if (duplicates.length > 0) {
        enqueueSnackbar(`${duplicates.length} potential duplicate${duplicates.length > 1 ? 's' : ''} detected. Review before adding.`, { variant: 'warning' });
        if (onDuplicatesDetected) {
          onDuplicatesDetected({
            duplicates: duplicates,
            fallbackEntities: [reqBody],
          });
        }
        resetFields();
        handleClose();
        return;
      }
      enqueueSnackbar('Entity added successfully', { variant: 'success' });
      resetFields();
      handleClose();
      if (handleRefresh) {
        handleRefresh();
      }
      onEntityAdded?.();
    } catch (error: any) {
      console.error('Error adding entity:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Error adding entity';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Scrollable fields region */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingTop: '12px',
          paddingBottom: '12px',
        }}
      >
        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-head-cont-supporting'>
              <h1>Enter Entity information manually</h1>
              <p>Entity 1</p>
            </div>
            <div className='audit-add-modal-cont-body-frame-form-row'>
            <div className='grid-layout' style={{ width: '100%' }}>
              <div>
                <label
                  htmlFor='supplier-name'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Name <span className='required-star'>*</span>
                </label>
                <input
                  type='text'
                  id='supplier-name'
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder='Company name'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label
                  htmlFor='supplier-alias'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Alias
                </label>
                <input
                  type='text'
                  id='supplier-alias'
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  placeholder='Alias'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                />
              </div>
              {/* Entity type: business or person */}
              <div>
                <label
                  htmlFor='entity-type'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Type
                </label>
                <select
                  id='entity-type'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                >
                  <option value='company'>Business</option>
                  <option value='person'>Person</option>
                </select>
              </div>
              {/* Description */}
              <div className='col-span-2'>
                <label
                  htmlFor='supplier-description'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Description
                </label>
                <textarea
                  id='supplier-description'
                  rows={3}
                  placeholder='Brief description'
                  value={supplierDescription}
                  onChange={(e) => setSupplierDescription(e.target.value)}
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                ></textarea>
              </div>
              <div>
                <label
                  htmlFor='contact-email'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Contact Email
                </label>
                <input
                  type='email'
                  id='contact-email'
                  placeholder='contact@company.com'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              {/* Contact Phone */}
              <div>
                <label
                  htmlFor='contact-phone'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Contact Phone
                </label>
                <input
                  type='tel'
                  id='contact-phone'
                  placeholder='+1-555-0123'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
              {/* Address */}
              <div>
                <label
                  htmlFor='supplier-address'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Address
                </label>
                <input
                  type='text'
                  id='supplier-address'
                  placeholder='Street address'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              {/* Address 1 */}
              <div>
                <label
                  htmlFor='supplier-address1'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Address 1
                </label>
                <input
                  type='text'
                  id='supplier-address1'
                  placeholder='Additional address line 1'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                />
              </div>
              {/* Address 2 */}
              <div>
                <label
                  htmlFor='supplier-address2'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Address 2
                </label>
                <input
                  type='text'
                  id='supplier-address2'
                  placeholder='Additional address line 2'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                />
              </div>
              {/* Country */}
              <div>
                <label
                  htmlFor='supplier-country'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Country
                </label>
                <input
                  type='text'
                  id='supplier-country'
                  placeholder='Country'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
              {/* Business Unit */}
              <div>
                <label
                  htmlFor='business-unit'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Business Unit
                </label>
                <input
                  type='text'
                  id='business-unit'
                  placeholder='Business unit'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={businessUnit}
                  onChange={(e) => setBusinessUnit(e.target.value)}
                />
              </div>
              {/* Parent Business */}
              <div>
                <label
                  htmlFor='parent-business'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Parent Business
                </label>
                <input
                  type='text'
                  id='parent-business'
                  placeholder='Parent business'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={parentBusiness}
                  onChange={(e) => setParentBusiness(e.target.value)}
                />
              </div>
              {/* SKU / Product ID */}
              <div>
                <label
                  htmlFor='sku-product-id'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  SKU / Product ID
                </label>
                <input
                  type='text'
                  id='sku-product-id'
                  placeholder='SKU or Product ID'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={skuProductId}
                  onChange={(e) => setSkuProductId(e.target.value)}
                />
              </div>
              {/* Supplier / Vendor ID */}
              <div>
                <label
                  htmlFor='supplier-vendor-id'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Supplier / Vendor ID
                </label>
                <input
                  type='text'
                  id='supplier-vendor-id'
                  placeholder='Supplier or Vendor ID'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={supplierVendorId}
                  onChange={(e) => setSupplierVendorId(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor='business-registration-number'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Business Registration Number / (EIN)
                </label>
                <input
                  type='text'
                  id='business-registration-number'
                  placeholder='Business registration number or EIN'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={businessRegistrationNumber}
                  onChange={(e) => setBusinessRegistrationNumber(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor='person-id'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Person ID
                </label>
                <input
                  type='text'
                  id='person-id'
                  placeholder='Person identifier'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={personId}
                  onChange={(e) => setPersonId(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor='manufacturer-identification-number'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Manufacturer Identification Number (MID)
                </label>
                <input
                  type='text'
                  id='manufacturer-identification-number'
                  placeholder='Manufacturer identification number'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={manufacturerIdentificationNumber}
                  onChange={(e) => setManufacturerIdentificationNumber(e.target.value)}
                />
              </div>
              {/* Domain */}
              <div>
                <label
                  htmlFor='entity-domain'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Domain
                </label>
                <input
                  type='text'
                  id='entity-domain'
                  placeholder='company.com'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%' }}
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                />
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pinned footer */}
      <div
        className='audit-modal-cont-footer'
        style={{
          flexShrink: 0,
          paddingTop: '16px',
          borderTop: '1px solid var(--bg-border-db)',
        }}
      >
        <div
          className='audit-modal-cont-footer-content'
          style={{ paddingTop: '16px' }}
        >
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={handleCancel}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={
              !createEntityMutation.isPending ?
                handleSupplierAddition :
                undefined
            }
          >
            <div
              className={`audit-modal-cont-footer-content-btn-text ${
                createEntityMutation.isPending ?
                  'opacity-50 cursor-not-allowed' :
                  ''
              }`}
            >
              {createEntityMutation.isPending ? (
                <span className='flex items-center justify-center'>
                  <CircularProgress
                    style={{
                      color: '#FFF',
                      width: '25px',
                      height: '25px',
                      marginRight: '10px',
                    }}
                  />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleEntityForm;
